import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './layouts/Layout';
import { Dashboard } from './pages/Dashboard';
import { ExamsList } from './pages/ExamsList';
import { ExamForm } from './pages/ExamForm';
import { BlueprintEditor } from './pages/BlueprintEditor';
import { CoursesList } from './pages/CoursesList';
import { CourseForm } from './pages/CourseForm';
import { TestsList } from './pages/TestsList';
import { TestForm } from './pages/TestForm';
import { MaterialsList } from './pages/MaterialsList';
import { QuestionTypeConfig } from './pages/QuestionTypeConfig';
import { GenerateQuestions } from './pages/GenerateQuestions';
import QuestionGeneratorCenter from './pages/QuestionGeneratorCenter';
import { ReviewQueue } from './pages/ReviewQueue';
import { QuestionBank } from './pages/QuestionBank';
import { QuestionEditForm } from './pages/QuestionEditForm';
import { Analytics } from './pages/Analytics';
import { AnalyticsCosts } from './pages/AnalyticsCosts';
import { AnalyticsRAG } from './pages/AnalyticsRAG';
import { AnalyticsCache } from './pages/AnalyticsCache';
import { SystemConfig } from './pages/settings/SystemConfig';
import { APIConfig } from './pages/settings/APIConfig';
import { RAGConfig } from './pages/settings/RAGConfig';
import { DropdownConfig } from './pages/settings/DropdownConfig';
import { UserManagement } from './pages/settings/UserManagement';
import { RolesManagement } from './pages/settings/RolesManagement';
import { RoleManagement } from './pages/settings/RoleManagement';
import { RAGDashboard } from './pages/rag/RAGDashboard';
import { AgentsConfig } from './pages/rag/AgentsConfig';
import { ToolsConfig } from './pages/rag/ToolsConfig';
import { QueryPlayground } from './pages/rag/QueryPlayground';
import { ValidationDashboard } from './pages/rag/ValidationDashboard';
import { ExecutionMonitor } from './pages/rag/ExecutionMonitor';
import { TraceExplorer } from './pages/rag/TraceExplorer';
import { CostManagement } from './pages/rag/CostManagement';
import { WorkflowBuilder } from './pages/rag/WorkflowBuilder';
import SharePointSync from './pages/SharePointSync';
import SlidesMaker from './pages/SlidesMaker';
import GeneratedSlidesList from './pages/GeneratedSlidesList';
import PptxConverter from './pages/PptxConverter';
import ConvertedPptxList from './pages/ConvertedPptxList';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './contexts/ToastContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { useAuthStore } from './store/authStore';
import Login from './pages/auth/Login';
import { NotFound } from './pages/NotFound';
import { PdfAnalysisList } from './pages/pdf-analysis/PdfAnalysisList';
import { PdfAnalysisDetail } from './pages/pdf-analysis/PdfAnalysisDetail';
import { PdfAnalysisQuestions } from './pages/pdf-analysis/PdfAnalysisQuestions';
import { PdfQuestionBank } from './pages/pdf-analysis/PdfQuestionBank';
import { PdfQuestionGenerator } from './pages/pdf-analysis/PdfQuestionGenerator';
import { PortfoliosManagement } from './pages/portfolios/PortfoliosManagement';
import { PdfQuestionEdit } from './pages/pdf-analysis/PdfQuestionEdit';
import Chatbot from './pages/Chatbot';
import ChatbotFeedback from './pages/ChatbotFeedback';
import PineconeData from './pages/PineconeData';
import ContentAssemblyDashboard from './pages/content-assembly/ContentAssemblyDashboard';
import { ReviewTasks } from './pages/editorial/ReviewTasks';
import { ReviewTaskDetail } from './pages/editorial/ReviewTaskDetail';
import { AIReviewMonitor } from './pages/editorial/AIReviewMonitor';
import { PromptManagement } from './pages/editorial/PromptManagement';
import { RejectionAnalytics } from './pages/editorial/RejectionAnalytics';

function App() {
  const { isAuthenticated, loadUser } = useAuthStore();

  // Load user data on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUser();
    }
  }, [isAuthenticated, loadUser]);

  return (
    <BrowserRouter>
      <ToastProvider>
        <ErrorBoundary>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            {/* Registration disabled - users created by admin only */}
            {/* <Route path="/register" element={<Register />} /> */}

            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />

          {/* Exams */}
          <Route path="exams" element={<ExamsList />} />
          <Route path="exams/create" element={<ExamForm />} />
          <Route path="exams/:id/edit" element={<ExamForm />} />
          <Route path="exams/:id/blueprint" element={<BlueprintEditor />} />

          {/* Courses */}
          <Route path="courses" element={<CoursesList />} />
          <Route path="courses/create" element={<CourseForm />} />
          <Route path="courses/:id/edit" element={<CourseForm />} />

          {/* Tests */}
          <Route path="tests" element={<TestsList />} />
          <Route path="tests/create" element={<TestForm />} />
          <Route path="tests/:id/edit" element={<TestForm />} />

          {/* Materials */}
          <Route path="materials" element={<MaterialsList />} />

          {/* PDF Analysis */}
          <Route path="pdf-analysis" element={
            <ProtectedRoute requiredPermissions={['view_questions']}>
              <PdfAnalysisList />
            </ProtectedRoute>
          } />
          <Route path="pdf-analysis/:id" element={<PdfAnalysisDetail />} />
          <Route path="pdf-analysis/:id/questions" element={<PdfAnalysisQuestions />} />
          <Route path="pdf-analysis/question-bank" element={
            <ProtectedRoute requiredPermissions={['view_questions']}>
              <PdfQuestionBank />
            </ProtectedRoute>
          } />
          <Route path="pdf-analysis/generate" element={
            <ProtectedRoute requiredPermissions={['create_questions', 'generate_tests']}>
              <PdfQuestionGenerator />
            </ProtectedRoute>
          } />
          <Route path="pdf-analysis/questions/:id/edit" element={<PdfQuestionEdit />} />
          <Route path="pdf-analysis/portfolios" element={
            <ProtectedRoute requiredPermissions={['view_questions']}>
              <PortfoliosManagement />
            </ProtectedRoute>
          } />

          {/* SharePoint Sync */}
          <Route path="sharepoint-sync" element={
            <ProtectedRoute requiredPermissions={['upload_pdf']}>
              <SharePointSync />
            </ProtectedRoute>
          } />

          {/* Slides Maker */}
          <Route path="slides-maker" element={
            <ProtectedRoute requiredPermissions={['slides_maker_view']}>
              <SlidesMaker />
            </ProtectedRoute>
          } />
          <Route path="slides-history" element={
            <ProtectedRoute requiredPermissions={['slides_history_view']}>
              <GeneratedSlidesList />
            </ProtectedRoute>
          } />

          {/* PPTX Converter */}
          <Route path="pptx-converter" element={
            <ProtectedRoute requiredPermissions={['pptx_converter_view']}>
              <PptxConverter />
            </ProtectedRoute>
          } />
          <Route path="pptx-history" element={
            <ProtectedRoute requiredPermissions={['pptx_history_view']}>
              <ConvertedPptxList />
            </ProtectedRoute>
          } />

          {/* Chatbot */}
          <Route path="chatbot" element={<Chatbot />} />
          <Route path="chatbot/feedback" element={<ChatbotFeedback />} />
          <Route path="chatbot/pinecone-data" element={<PineconeData />} />

          {/* Editorial Workflow (NWFE-RQ-01) */}
          <Route path="editorial/review-tasks" element={
            <ProtectedRoute requiredPermissions={['review_task_view']}>
              <ReviewTasks />
            </ProtectedRoute>
          } />
          <Route path="editorial/review-tasks/:id" element={
            <ProtectedRoute requiredPermissions={['review_task_view']}>
              <ReviewTaskDetail />
            </ProtectedRoute>
          } />
          <Route path="editorial/rejections" element={
            <ProtectedRoute requiredPermissions={['review_task_view']}>
              <RejectionAnalytics />
            </ProtectedRoute>
          } />
          <Route path="editorial/ai-review" element={
            <ProtectedRoute requiredPermissions={['ai_review_monitor']}>
              <AIReviewMonitor />
            </ProtectedRoute>
          } />
          <Route path="editorial/ai-prompts" element={
            <ProtectedRoute requiredPermissions={['prompt_manage']}>
              <PromptManagement />
            </ProtectedRoute>
          } />

          {/* Content Assembly */}
          <Route path="content-assembly" element={
            <ProtectedRoute requiredPermissions={['edit_all_questions']}>
              <ContentAssemblyDashboard />
            </ProtectedRoute>
          } />

          {/* Configuration */}
          <Route path="config/question-types" element={<QuestionTypeConfig />} />

          {/* Generation */}
          <Route path="generate" element={<GenerateQuestions />} />
          <Route path="generator-center" element={<QuestionGeneratorCenter />} />

          {/* Question Bank */}
          <Route path="questions" element={<QuestionBank />} />
          <Route path="questions/:id/edit" element={<QuestionEditForm />} />

          {/* Review */}
          <Route path="review" element={<ReviewQueue />} />

          {/* Analytics */}
          <Route path="analytics" element={<Analytics />} />
          <Route path="analytics/costs" element={<AnalyticsCosts />} />
          <Route path="analytics/rag" element={<AnalyticsRAG />} />
          <Route path="analytics/cache" element={<AnalyticsCache />} />

          {/* RAG System */}
          <Route path="rag/dashboard" element={<RAGDashboard />} />
          <Route path="rag/agents" element={<AgentsConfig />} />
          <Route path="rag/tools" element={<ToolsConfig />} />
          <Route path="rag/playground" element={<QueryPlayground />} />
          <Route path="rag/validation" element={<ValidationDashboard />} />
          <Route path="rag/executions" element={<ExecutionMonitor />} />
          <Route path="rag/traces" element={<TraceExplorer />} />
          <Route path="rag/costs" element={<CostManagement />} />
          <Route path="rag/workflows" element={<WorkflowBuilder />} />

          {/* Settings */}
          <Route path="settings/system" element={
            <ProtectedRoute requiredPermissions={['system_config']}>
              <SystemConfig />
            </ProtectedRoute>
          } />
          <Route path="settings/api" element={
            <ProtectedRoute requiredPermissions={['system_config']}>
              <APIConfig />
            </ProtectedRoute>
          } />
          <Route path="settings/rag" element={
            <ProtectedRoute requiredPermissions={['system_config']}>
              <RAGConfig />
            </ProtectedRoute>
          } />
          <Route path="settings/dropdowns" element={<DropdownConfig />} />
          <Route path="settings/users" element={
            <ProtectedRoute requiredPermissions={['user_management_view']}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="settings/permissions" element={
            <ProtectedRoute requiredPermissions={['permissions_management_view']}>
              <RolesManagement />
            </ProtectedRoute>
          } />
          <Route path="settings/role-management" element={
            <ProtectedRoute requiredPermissions={['role_management_view']}>
              <RoleManagement />
            </ProtectedRoute>
          } />

          {/* 404 - Catch all undefined routes (inside protected routes) */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* 404 - Catch all undefined routes (outside protected routes) */}
        <Route path="*" element={<NotFound />} />
      </Routes>
        </ErrorBoundary>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
